import { AutoAwesome, CloudUpload, Edit, EmojiObjects, Psychology, School, WorkOutline } from '@mui/icons-material';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import SkillTileWidget from '../../Widgets/SkillTileWidget';
import LevelSelectorButtons from '../components/LevelSelectorButtons';
import { useOnboardingStrings } from '../data/OnboardingLangStrings';
import type { CertificateItem, ResultsOverviewStepProps, SkillItem } from '../types/OnboardingTypes';

const ResultsOverviewStep: React.FC<ResultsOverviewStepProps> = ({
    data,
    onNext,
    onBack,
    setAgentMessage,
    onDataUpdate,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const strings = useOnboardingStrings('de');
    const [editingSkill, setEditingSkill] = useState<number | null>(null);

    React.useEffect(() => {
        setAgentMessage(strings.resultsMessage);
    }, [setAgentMessage, strings.resultsMessage]);

    // Get top skills (level 3 and 4) for Super Powers
    const topSkills = data.selectedItems.skills
        .filter((skill: SkillItem) => skill.level >= 3)
        .sort((a: SkillItem, b: SkillItem) => b.level - a.level)
        .slice(0, 6);

    const handleLevelChange = (skillId: number, newLevel: 1 | 2 | 3 | 4) => {
        const updatedSkills = data.selectedItems.skills.map((skill: any) =>
            skill.id === skillId ? { ...skill, level: newLevel } : skill,
        );

        // Also update analysisResults to persist changes
        const updatedAnalysisSkills = data.analysisResults.skills.map((skill: any) =>
            skill.id === skillId ? { ...skill, level: newLevel } : skill,
        );

        if (onDataUpdate) {
            onDataUpdate({
                ...data,
                analysisResults: {
                    ...data.analysisResults,
                    skills: updatedAnalysisSkills,
                },
                selectedItems: {
                    ...data.selectedItems,
                    skills: updatedSkills,
                },
            });
        }
        setEditingSkill(null);
    };

    const getCertificateLogo = (cert: CertificateItem) => {
        if (cert.customLogo) return cert.customLogo;
        const name = cert.name.toLowerCase();
        if (name.includes('aws')) return '/certificates/aws-logo.svg';
        if (name.includes('react')) return '/certificates/react-logo.svg';
        if (name.includes('scrum') || name.includes('agile')) return '/certificates/scrum-logo.svg';
        if (name.includes('google')) return '/certificates/google-logo.svg';
        if (name.includes('microsoft') || name.includes('azure')) return '/certificates/microsoft-logo.svg';
        return '/certificates/default-cert.svg';
    };

    const handleLogoUpload = (certId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const logoUrl = e.target?.result as string;
                const updatedCertificates = data.selectedItems.certificates.map((cert: any) =>
                    cert.id === certId ? { ...cert, customLogo: logoUrl } : cert,
                );

                if (onDataUpdate) {
                    onDataUpdate({
                        ...data,
                        selectedItems: {
                            ...data.selectedItems,
                            certificates: updatedCertificates,
                        },
                    });
                }
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <Box
            sx={{
                maxWidth: 1200,
                mx: 'auto',
                px: isMobile ? 1 : 0,
            }}
        >
            <Typography
                variant={isMobile ? 'h5' : 'h4'}
                sx={{ fontWeight: 600, mb: 1, textAlign: isMobile ? 'center' : 'left' }}
            >
                Ihre Talent-Übersicht
            </Typography>
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: isMobile ? 2 : 4, textAlign: isMobile ? 'center' : 'left' }}
            >
                Hier ist dein persönliches Kompetenzprofil mit deinen herausragenden Fähigkeiten.
            </Typography>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {/* Personal Info Card */}
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '1 1 300px' },
                        minWidth: isMobile ? 'auto' : 300,
                        order: isMobile ? 1 : 0,
                    }}
                >
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        }}
                    >
                        <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                                <Avatar
                                    sx={{
                                        width: 60,
                                        height: 60,
                                        bgcolor: theme.palette.primary.main,
                                        fontSize: '1.5rem',
                                        fontWeight: 600,
                                    }}
                                >
                                    {data.personalInfo.firstName.charAt(0)}
                                    {data.personalInfo.lastName.charAt(0)}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                        {data.personalInfo.firstName} {data.personalInfo.lastName}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Talent-Profil
                                    </Typography>
                                </Box>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 3, lineHeight: 1.6 }}>
                                {data.personalInfo.shortDescription}
                            </Typography>

                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                <Chip
                                    icon={<Psychology />}
                                    label={`${data.selectedItems.skills.length} Skills`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<EmojiObjects />}
                                    label={`${topSkills.length} SuperPowers`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<School />}
                                    label={`${data.selectedItems.certificates.length} Zertifikate`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                                <Chip
                                    icon={<WorkOutline />}
                                    label={`${data.selectedItems.roles.length} Rollen`}
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

                {/* Super Powers Card */}
                <Box
                    sx={{
                        flex: { xs: '1 1 100%', md: '2 1 500px' },
                        minWidth: isMobile ? 'auto' : 500,
                        order: isMobile ? 2 : 1,
                    }}
                >
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            border: `2px solid ${theme.palette.warning.main}`,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.light, 0.05)} 100%)`,
                            position: 'relative',
                            overflow: 'visible',
                        }}
                    >
                        {/* Super Powers Badge */}
                        <Box
                            sx={{
                                position: 'absolute',
                                top: -12,
                                left: 24,
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                background: `linear-gradient(45deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                boxShadow: `0 4px 12px ${alpha(theme.palette.warning.main, 0.4)}`,
                            }}
                        >
                            <AutoAwesome sx={{ fontSize: 16 }} />
                            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                                {strings.superPowers.toUpperCase()}
                            </Typography>
                        </Box>

                        <CardContent sx={{ p: 3, pt: 4 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: theme.palette.warning.dark }}>
                                Ihre herausragenden {strings.skills}
                            </Typography>

                            {topSkills.length > 0 ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {topSkills.map((skill: any) => (
                                        <Box
                                            key={skill.id}
                                            sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)' }, minWidth: 250 }}
                                        >
                                            <SkillTileWidget
                                                id={skill.id}
                                                name={skill.name}
                                                level={skill.level}
                                                category={skill.category}
                                                type="skill"
                                                showStar={true}
                                                onEdit={() => setEditingSkill(skill.id)}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    {strings.noSuperPowers}
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                {/* All Skills Overview */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? '16px' : 3,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.primary.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <Psychology sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                Alle {strings.skills} ({data.selectedItems.skills.length})
                            </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {data.selectedItems.skills.map((skill: any, index: number) => (
                                <Box key={skill.id}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            py: 1,
                                        }}
                                    >
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{ fontWeight: 500, fontSize: isMobile ? '0.85rem' : undefined }}
                                            >
                                                {skill.name}
                                            </Typography>
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                                            >
                                                {skill.category}
                                            </Typography>
                                        </Box>

                                        {editingSkill === skill.id ? (
                                            <LevelSelectorButtons
                                                level={skill.level}
                                                onChange={(newLevel) => handleLevelChange(skill.id, newLevel)}
                                            />
                                        ) : (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Chip
                                                    label={
                                                        skill.level === 1
                                                            ? 'Junior'
                                                            : skill.level === 2
                                                              ? 'Mid-Level'
                                                              : skill.level === 3
                                                                ? 'Senior'
                                                                : 'Expert'
                                                    }
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: `var(--color-skill-${skill.level === 1 ? 'junior' : skill.level === 2 ? 'intermediate' : skill.level === 3 ? 'senior' : 'principal'})`,
                                                        color: skill.level >= 4 ? '#ffffff' : '#1a1a1a',
                                                    }}
                                                />
                                                <Button
                                                    size="small"
                                                    onClick={() => setEditingSkill(skill.id)}
                                                    sx={{ minWidth: 'auto', px: 1 }}
                                                >
                                                    <Edit sx={{ fontSize: 14 }} />
                                                </Button>
                                            </Box>
                                        )}
                                    </Box>
                                    {index < data.selectedItems.skills.length - 1 && <Divider sx={{ my: 1 }} />}
                                </Box>
                            ))}
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mt: 3 }}>
                {/* Certificates with Logos */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? '16px' : 3,
                            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.info.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <School sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                {strings.certificates} ({data.selectedItems.certificates.length})
                            </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {data.selectedItems.certificates.length > 0 ? (
                                data.selectedItems.certificates.map((cert: any, index: number) => (
                                    <Box key={cert.id}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
                                            <Avatar
                                                src={getCertificateLogo(cert)}
                                                sx={{
                                                    width: 60,
                                                    height: 60,
                                                    bgcolor: alpha(theme.palette.secondary.main, 0.1),
                                                }}
                                            >
                                                <School />
                                            </Avatar>
                                            <Box sx={{ flex: 1 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontWeight: 500, fontSize: isMobile ? '0.85rem' : undefined }}
                                                >
                                                    {cert.name}
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                                                >
                                                    Zertifikat{' '}
                                                    {cert.status === 'accepted' ? '✓ Bestätigt' : '⏳ Ausstehend'}
                                                </Typography>
                                            </Box>
                                            {cert.status === 'accepted' && (
                                                <Box>
                                                    <input
                                                        accept="image/*"
                                                        style={{ display: 'none' }}
                                                        id={`logo-upload-${cert.id}`}
                                                        type="file"
                                                        onChange={(e) => handleLogoUpload(cert.id, e)}
                                                    />
                                                    <label htmlFor={`logo-upload-${cert.id}`}>
                                                        <IconButton
                                                            component="span"
                                                            size="small"
                                                            sx={{
                                                                color: theme.palette.secondary.main,
                                                                '&:hover': {
                                                                    backgroundColor: alpha(
                                                                        theme.palette.secondary.main,
                                                                        0.1,
                                                                    ),
                                                                },
                                                            }}
                                                        >
                                                            <CloudUpload sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </label>
                                                </Box>
                                            )}
                                        </Box>
                                        {index < data.selectedItems.certificates.length - 1 && (
                                            <Divider sx={{ my: 1 }} />
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic', fontSize: isMobile ? '0.85rem' : undefined }}
                                >
                                    Keine Zertifikate ausgewählt
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
                {/* Roles */}
                <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 400px' }, minWidth: isMobile ? 'auto' : 400 }}>
                    <Box
                        sx={{
                            height: '100%',
                            p: isMobile ? '16px' : 3,
                            border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
                            borderRadius: 2,
                            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                            backdropFilter: 'blur(10px)',
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: isMobile ? 2 : 3 }}>
                            <Avatar
                                sx={{
                                    bgcolor: theme.palette.success.main,
                                    width: isMobile ? 28 : 40,
                                    height: isMobile ? 28 : 40,
                                }}
                            >
                                <WorkOutline sx={{ fontSize: isMobile ? 16 : 24 }} />
                            </Avatar>
                            <Typography
                                variant="h6"
                                sx={{ fontWeight: 600, fontSize: isMobile ? '0.9rem' : undefined }}
                            >
                                Gewünschte {strings.roles} ({data.selectedItems.roles.length})
                            </Typography>
                        </Box>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                            {data.selectedItems.roles.length > 0 ? (
                                data.selectedItems.roles.map((role: any, index: number) => (
                                    <Box key={role.id}>
                                        <Box
                                            sx={{
                                                p: 2,
                                                borderRadius: 2,
                                                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                                                background: alpha(theme.palette.info.main, 0.05),
                                                mb: index < data.selectedItems.roles.length - 1 ? 2 : 0,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontWeight: 600,
                                                    mb: 1,
                                                    fontSize: isMobile ? '0.85rem' : undefined,
                                                }}
                                            >
                                                {role.name}
                                            </Typography>
                                            {role.description && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{ fontSize: isMobile ? '0.75rem' : undefined }}
                                                >
                                                    {role.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>
                                ))
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ fontStyle: 'italic', fontSize: isMobile ? '0.85rem' : undefined }}
                                >
                                    Keine Rollen ausgewählt
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Box>
            </Box>

            {/* Navigation */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between',
                    gap: isMobile ? 2 : 0,
                    mt: 4,
                    mb: isMobile ? 10 : 0,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onBack}
                    fullWidth={isMobile}
                    sx={{ px: 3 }}
                    aria-label="Zurück zur Bearbeitung"
                >
                    {strings.back} zur Bearbeitung
                </Button>
                <Button
                    variant="contained"
                    size="medium"
                    onClick={onNext}
                    fullWidth={isMobile}
                    aria-label="Weiter zur Fertigstellung"
                    sx={{
                        px: 4,
                        py: 1.5,
                        fontSize: isMobile ? '1rem' : '1.1rem',
                        fontWeight: 600,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 40px ${alpha(theme.palette.primary.main, 0.4)}`,
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    Weiter zur {strings.completion}
                </Button>
            </Box>
        </Box>
    );
};

export default ResultsOverviewStep;
